import React, { createContext, useContext } from 'react'

import { useAlertStyle, useAlertIconStyle } from '../../hooks/useAlertStyle'
import { Box, BoxProps } from '../Box'
import { Icon } from '../Icon'

export const statuses = {
  info: { icon: 'info', color: 'blue' },
  warning: { icon: 'warning-2', color: 'orange' },
  success: { icon: 'check-circle', color: 'green' },
  error: { icon: 'warning', color: 'red' },
  passive: { icon: '', color: 'gray' },
}

export interface AlertProps extends BoxProps {
  /**
   * The status of the alert
   */
  status?: 'error' | 'success' | 'warning' | 'info' | 'passive'
  /**
   * The variant of the alert style to use.
   */
  variant?: 'subtle' | 'solid' | 'left-accent' | 'top-accent'
}

const AlertContext = createContext(undefined)

const useAlertContext = () => {
  const context = useContext(AlertContext)
  if (context === undefined) {
    throw new Error('useAlertContext must be used within a AlertContextProvider')
  }
  return context
}

export const Alert = ({ status = 'info', variant = 'subtle', ...restProps }: AlertProps) => {
  const alertStyleProps = useAlertStyle({
    variant,
    color: statuses[status] && statuses[status]['color'],
  })

  const context = { status, variant }

  return (
    <AlertContext.Provider value={context}>
      <Box role="alert" position="relative" {...alertStyleProps} {...restProps} />
    </AlertContext.Provider>
  )
}

export const AlertTitle = props => <Box fontWeight="bold" lineHeight="normal" {...props} />
export const AlertDescription = props => <Box {...props} />

export const AlertIcon = props => {
  const { status, variant } = useAlertContext()
  const iconStyleProps = useAlertIconStyle({
    variant,
    color: statuses[status] && statuses[status]['color'],
  })

  return (
    <Icon
      // mt={1}
      mr={3}
      size={5}
      name={statuses[status]?.icon}
      {...iconStyleProps}
      {...props}
    />
  )
}
